import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/lib/data";
import { OpenStatus } from "@/components/OpenStatus";

const SHOP_URL = "https://shop.lacontadina-dresden.de/";
const RESERVE_URL = "https://reservierung.lacontadina-dresden.de";

const links = [
  { to: "/", label: "Home" },
  { href: SHOP_URL, label: "Speisen bestellen", external: true },
  { to: "/ueber-uns", label: "Über uns" },
  { to: "/olivenoel", label: "Olivenöl" },
  { to: "/gutschein", label: "Gutschein" },
  { to: "/karriere", label: "Karriere" },
  { to: "/kontakt", label: "Kontakt" },
];

const slug = (label) => label.toLowerCase().replace(/[^a-z]/g, "");

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-cream/85 border-b border-gold/20 py-3" : "py-5 bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={BRAND.logo} alt="La Contadina" className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover ring-1 ring-gold/40 transition-transform duration-500 group-hover:scale-105 group-hover:rotate-3" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className={`text-[10px] tracking-[0.25em] uppercase transition-colors ${scrolled ? "text-ink-muted" : "text-cream/80"}`}>{BRAND.tagline}</span>
            <span className={`font-playball text-2xl md:text-3xl tracking-tight leading-tight transition-colors ${scrolled ? "text-wine" : "text-cream"}`}>{BRAND.name}</span>
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-5">
          {links.map((l) =>
            l.external ? (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-link-${slug(l.label)}`}
                className={`link-underline whitespace-nowrap text-[11px] tracking-[0.18em] uppercase transition-colors ${scrolled ? "text-ink-soft hover:text-wine" : "text-cream/90 hover:text-gold"}`}
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${slug(l.label)}`}
                className={({ isActive }) =>
                  `link-underline whitespace-nowrap text-[11px] tracking-[0.18em] uppercase transition-colors ${
                    scrolled
                      ? (isActive ? "text-wine" : "text-ink-soft hover:text-wine")
                      : (isActive ? "text-gold" : "text-cream/90 hover:text-gold")
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <span className="bg-ink/80 backdrop-blur px-3 py-1.5 rounded-full"><OpenStatus dark /></span>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className={`xl:hidden transition-colors ${scrolled || open ? "text-wine" : "text-cream"}`}
          aria-label="Menü"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-cream border-t border-gold/20 px-6 py-6 flex flex-col gap-5">
          <span className="bg-ink/80 px-3 py-1.5 rounded-full self-start"><OpenStatus dark /></span>
          {links.map((l) =>
            l.external ? (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                className="text-sm tracking-[0.2em] uppercase text-ink-soft"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `text-sm tracking-[0.2em] uppercase ${isActive ? "text-wine" : "text-ink-soft"}`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <a href={RESERVE_URL} target="_blank" rel="noreferrer" data-testid="mobile-reserve-btn" className="btn-pop bg-wine text-cream px-7 py-3 uppercase text-[11px] tracking-[0.2em] text-center">
            Tisch reservieren
          </a>
        </div>
      )}
    </header>
  );
};
